import { useCallback, useMemo, useState } from 'react';
import { Dialog, Button } from '@workspace/design-system/src/components';
import { usePlanInquiryList } from '@src/hooks/enterpriseInquiry/queries';
import type { PlanInquiryResponseModel } from '@src/models/enterprise-inquiry';
import { formatPhoneNumberForDisplay } from '@src/utils/phone';

export interface InquiryItem {
    id: string;
    name: string; // 고객/문의자 이름
    receivedAt: string; // yyyy-MM-dd
    customerName?: string;
    contactName?: string;
    contactTitle?: string;
    contactEmail?: string;
    contactPhone?: string;
    businessType?: string; // businessTypeOptions 중 하나
    memo?: string; // 문의 메모
}

interface InquirySearchModalProps {
    onClose: () => void;
    onSelectInquiry: (inquiry: InquiryItem) => void;
}

/**
 * PlanInquiryResponseModel을 InquiryItem으로 변환하는 함수
 */
const transformInquiry = (inquiry: PlanInquiryResponseModel): InquiryItem => {
    // 날짜 포맷팅: ISO 문자열을 yyyy-MM-dd 형식으로 변환
    const formatDate = (dateString?: string | null): string => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        } catch {
            return '';
        }
    };

    // ApplicationBusinessType을 businessTypeOptions의 값으로 변환
    const convertBusinessType = (businessType?: string | null): string => {
        if (!businessType) return '';
        
        // None은 'none'으로 변환
        if (businessType === 'None') {
            return 'none';
        }
        
        // 나머지는 매핑
        const businessTypeMap: Record<string, string> = {
            'General': 'company',
            'Agency': 'agency',
            'School': 'k12',
            'University': 'university',
            'Government': 'public',
            'Museum': 'library',
        };
        
        return businessTypeMap[businessType] || businessType.toLowerCase();
    };

    return {
        id: inquiry.id,
        name: inquiry.organizationName,
        receivedAt: formatDate(inquiry.createdAt),
        customerName: inquiry.organizationName,
        contactName: inquiry.contactName,
        contactTitle: inquiry.jobTitle,
        contactEmail: inquiry.email,
        contactPhone: inquiry.phone ? formatPhoneNumberForDisplay(inquiry.phone) : undefined,
        businessType: convertBusinessType(inquiry.businessType),
        memo: inquiry.memo || undefined,
    };
};

export function InquirySearchModal({ onClose, onSelectInquiry }: InquirySearchModalProps) {
    const [searchInput, setSearchInput] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    // API 호출 - 검색어가 있을 때만 활성화
    const inquiryQuery = usePlanInquiryList(
        {
            page: 1,
            limit: 20, // 모달에서는 적당한 수의 결과만 표시
            searchTerm: searchTerm || undefined,
        },
        {
            queryOptions: {
                enabled: !!searchTerm.trim(), // 검색어가 있을 때만 호출
            },
        }
    );

    // API 응답을 InquiryItem으로 변환
    const results = useMemo<InquiryItem[]>(() => {
        if (!inquiryQuery.data?.inquiries) {
            return [];
        }
        return inquiryQuery.data.inquiries.map(transformInquiry);
    }, [inquiryQuery.data?.inquiries]);

    const handleSearch = useCallback(() => {
        const term = searchInput.trim();
        if (!term) {
            setSearchTerm('');
            return;
        }
        setSearchTerm(term);
    }, [searchInput]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
                handleSearch();
            }
        },
        [handleSearch]
    );

    return (
        <Dialog>
            <Dialog.Content className="w-[480px] !top-[20vh] !translate-y-0">
                {/* 검색 입력 */}
                <div className="p-6 flex flex-col gap-4">
                    <div className="flex gap-2 items-center">
                        <div className="flex-1 bg-[#F5F5F5] h-[40px] flex items-center px-4 rounded-lg border border-[#101010]">
                            <input
                                type="text"
                                className="w-full bg-transparent text-[14px] text-[#101010] leading-[20px] tracking-[-0.1px] outline-none placeholder:text-[#BFBFBF]"
                                placeholder="문의가 들어왔던 기업/단체명을 검색해 주세요"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                onKeyDown={handleKeyDown}
                            />
                        </div>
                        <Button
                            styleType="text-primary"
                            styleSize="XS-24"
                            onClick={handleSearch}
                            disabled={inquiryQuery.isLoading}
                            isLoading={inquiryQuery.isLoading}
                        >
                            문의 찾기
                        </Button>
                    </div>
                </div>

                {/* 검색 결과 */}
                {searchTerm && (
                    <div className="border-t border-[#F0F0F0]">
                        {inquiryQuery.isLoading ? (
                            <div className="px-6 py-8 text-center text-[#BFBFBF] text-[14px]">
                                검색 중...
                            </div>
                        ) : inquiryQuery.error ? (
                            <div className="px-6 py-8 text-center text-[#FF4D4F] text-[14px]">
                                검색 중 오류가 발생했습니다.
                            </div>
                        ) : results.length === 0 ? (
                            <div className="px-6 py-8 text-center text-[#BFBFBF] text-[14px]">
                                검색 결과가 없습니다.
                            </div>
                        ) : (
                            <div className="max-h-[240px] overflow-y-auto px-6 py-4 flex flex-col gap-6">
                                {results.map((item) => (
                                    <div key={item.id} className="flex gap-4 items-center">
                                        <div className="flex-1 flex gap-2 items-center">
                                            <div className="w-[32px] h-[32px] rounded-full bg-[#E0E0E0] flex items-center justify-center shrink-0">
                                                <span className="text-[14px] font-bold text-[#757575]">{item.name.charAt(0)}</span>
                                            </div>
                                            <div className="flex flex-col">
                                                <p className="text-[16px] text-[#101010] leading-[20px] tracking-[-0.1px]">
                                                    {item.name}
                                                </p>
                                                <p className="text-[12px] text-[#757575] leading-[16px] tracking-[-0.1px]">
                                                    문의 접수일: {item.receivedAt}
                                                </p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => onSelectInquiry(item)}
                                            className="bg-[#3C78F4] h-[32px] px-3 rounded-lg text-[14px] font-bold text-white leading-[16px] hover:bg-[#2d64dc] transition-colors"
                                        >
                                            불러오기
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </Dialog.Content>
        </Dialog>
    );
}


